import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, X, ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
        email,
        name,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-28 text-center md:px-8">
        <h1 className="font-display text-3xl font-light tracking-tight">Your cart is empty</h1>
        <p className="mt-3 text-sm text-zinc-500">Discover our curated beauty &amp; fashion essentials.</p>
        <Link to="/shop" className="mt-8 flex h-12 items-center gap-2 bg-zinc-900 px-8 text-xs uppercase tracking-widest text-white hover:bg-zinc-800">
          Continue Shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 md:px-8">
      <h1 className="font-display text-3xl font-light tracking-tight md:text-4xl">Shopping Cart</h1>
      <div className="mt-10 grid gap-12 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {items.map((i) => (
            <div key={i.id} className="flex gap-5 border-b border-zinc-200 py-6" data-testid={`cart-page-line-${i.id}`}>
              <Link to={`/product/${i.id}`} className="h-32 w-24 shrink-0 bg-zinc-100">
                {i.image && <img src={imgUrl(i.image)} alt={i.title} className="h-full w-full object-cover" />}
              </Link>
              <div className="flex flex-1 flex-col">
                <div className="flex justify-between gap-3">
                  <Link to={`/product/${i.id}`} className="font-display text-base hover:text-kkaccent">{i.title}</Link>
                  <button onClick={() => removeItem(i.id)} data-testid={`cart-page-remove-${i.id}`}>
                    <X size={18} className="text-zinc-400 hover:text-zinc-900" />
                  </button>
                </div>
                <p className="mt-1 text-sm text-zinc-500">{money(i.price)}</p>
                <div className="mt-auto flex items-center justify-between">
                  <div className="flex items-center border border-zinc-300">
                    <button className="px-3 py-2" onClick={() => updateQty(i.id, i.quantity - 1)}><Minus size={13} /></button>
                    <span className="min-w-8 text-center text-sm">{i.quantity}</span>
                    <button className="px-3 py-2" onClick={() => updateQty(i.id, i.quantity + 1)}><Plus size={13} /></button>
                  </div>
                  <span className="font-medium">{money(i.price * i.quantity)}</span>
                </div>
              </div>
            </div>
          ))}
          <Link to="/shop" className="mt-6 inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900">
            <ArrowLeft size={15} /> Continue Shopping
          </Link>
        </div>

        <div className="lg:col-span-1">
          <div className="border border-zinc-200 bg-white p-6">
            <h2 className="font-display text-lg">Order Summary</h2>
            <div className="mt-5 space-y-3 border-b border-zinc-200 pb-5 text-sm">
              <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span data-testid="summary-subtotal">{money(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Shipping</span><span>Calculated at checkout</span></div>
            </div>
            <div className="flex justify-between py-5 text-base font-medium">
              <span>Total</span><span data-testid="summary-total">{money(subtotal)}</span>
            </div>

            <div className="space-y-3">
              <Input placeholder="Full name (optional)" value={name} onChange={(e) => setName(e.target.value)} className="rounded-none border-zinc-300" data-testid="checkout-name" />
              <Input placeholder="Email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none border-zinc-300" data-testid="checkout-email" />
            </div>

            <Button
              onClick={checkout}
              disabled={loading}
              className="mt-4 h-12 w-full rounded-none bg-zinc-900 text-xs uppercase tracking-widest hover:bg-zinc-800"
              data-testid="proceed-checkout-btn"
            >
              {loading ? "Redirecting…" : "Proceed to Checkout"}
            </Button>
            <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-zinc-400">
              <Lock size={12} /> Secure payment powered by Stripe
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
